<script setup>
/* eslint camelcase: off */
import {
  defaultColor,
  formatPrecip,
  formatRelativeHumidity,
  getAlertColorForeground,
  getAlertColorPrimary,
  getAlertDescription,
  getElevationUnits,
  getSnowUnits,
  formatCloudCover,
  formatPrecipChance,
  formatTemp,
  formatWind,
  getWindUnits,
} from '@@/utils/CommonUtils';
import { useMetaStore } from '@@/stores/Meta';
import { useUserStore } from '@@/stores/User';

const props = defineProps({
  location: {
    type: Object,
    default: null,
  },
});

const $emit = defineEmits(['fetch-history-snow-detail']);

const $style = useCssModule();
const metaStore = useMetaStore();
const userStore = useUserStore();

const {
  getFeelsLikeAlert,
  getPowderAlert,
  getSnowLevelAlert,
  getWindGustAlert,
} = metaStore;

const units = computed(() => userStore.preferences.units);
const dayPeriods = ref([]);

const {
  isAllAccess,
  loadingClassName,
  selectedView,
  showLoading,
  updateSelectedView,
  view,
  views,
} = useCurrentHistoryToggle(props.location);

const populateData = async () => {
  const field = view.value === 'Current' ? 'forecast_semi_daily' : 'history_semi_daily';
  const snowUnits = getSnowUnits(units.value, true);

  // SEE: https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Math/max#getting_the_maximum_element_of_an_array
  let precipSnowMax = props.location[field]
    .reduce((acc, { precip_snow_max }) => Math
      .max(acc, Math.round(precip_snow_max)), 0);

  if (units.value === 'imperial' && precipSnowMax <= 10) {
    precipSnowMax = 10;
  }
  else if (units.value === 'metric' && precipSnowMax <= 25) {
    precipSnowMax = 25;
  }

  // Maximum length in pixels of the label 00-00" or 00-00cm. Used below for determining bar width
  const precipSnowRangeMaxLabelWidth = units.value === 'imperial' ? 45 : 60;

  props.location[field].forEach((dayPeriod, index) => {
    const {
      cloud_cover,
      conditions_icon_url,
      display_at_local_label,
      day_period,
      pop,
      precip_accum,
      precip_snow_max,
      precip_snow_min,
      relative_humidity,
      slr_label,
      snow_level,
      temp_apparent,
      temp_max,
      temp_min,
      wind_dir_label,
      wind_gust_speed,
      wind_speed,
    } = dayPeriod;

    // Set alerts
    const feelsLikeAlert = getFeelsLikeAlert(dayPeriod.alerts);
    const powderAlert = getPowderAlert(dayPeriod.alerts);
    const snowLevelAlert = getSnowLevelAlert(dayPeriod.alerts);
    const windGustAlert = getWindGustAlert(dayPeriod.alerts);

    const alerts = [];

    if (powderAlert?.show_badge ?? powderAlert?.level?.show_badge) {
      alerts.push(powderAlert);
    }

    if (snowLevelAlert?.show_badge ?? snowLevelAlert?.level?.show_badge) {
      alerts.push(snowLevelAlert);
    }

    if (windGustAlert?.show_badge ?? windGustAlert?.level?.show_badge) {
      alerts.push(windGustAlert);
    }

    alerts.sort((a, b) => a.sort_weight - b.sort_weight);

    // Set precip_snow_range value and color
    //
    // TODO: This seems like more logic than necessary. But snow level alerts appear to be
    // returned when the precip_snow_max > 0, but we want to ignore the snow level alert if the
    // precip_snow_max rounds to 0, because in that case we want to display 0" not the range
    // 0-0".

    const precip_snow_max_rounded = Math.round(precip_snow_max);
    const precip_snow_min_rounded = Math.round(precip_snow_min);
    const hasSnow = precip_snow_max_rounded > 0;
    const precip_snow_range = hasSnow
      ? `${precip_snow_min_rounded}-${precip_snow_max_rounded}${snowUnits}`
      : `0${snowUnits}`;

    const precip_snow_range_color = hasSnow
      ? getAlertColorForeground(powderAlert)
      : defaultColor;

    let precip_snow_bar_width = '2px';

    if (hasSnow) {
      // When the day period has snow then the bar width will be the bar percent of the
      // "available space" - which is 100% of width of the table cell minus the width of the
      // longest label, 00-00" or 00-00cm as mentioned above, plus 8px right margin on the bar.
      // This is an adaptation of the changes made in the iOS app are which are difficult to
      // directly implement in the web because the table cell size is not known when the page is
      // first rendered.
      // SEE: https://github.com/cloudninewx/OpenSnow-iOS-Neue/commit/cb748f620ccd812a425d924d3bc9706e16cb45f9
      const barPercent = Number(precip_snow_max_rounded / precipSnowMax).toFixed(2);
      precip_snow_bar_width = `calc(${barPercent} * (100% - ${Math.round(precipSnowRangeMaxLabelWidth + 8)}px))`;
    }

    // Set display_at_local_label
    //
    // The display_at_local_label will span two rows if the current semi daily forecast is for
    // the daytime and it's not the last record in the forecast_semi_daily array.

    const display_at_local_label_rowspan = day_period === 'day'
      && index + 1 < props.location.forecast_semi_daily.length
      ? 2
      : 1;

    // The display_at_local_label will be not be rendered if the current semi daily forecast is
    // for the nightime and it's not the first record in the forecast_semi_daily array.

    const skip_display_at_local_label = day_period === 'night' && index !== 0;

    //
    // Set the bottom and top border of each <td> element
    //

    let border = '';

    if ((day_period === 'night' && index === 0)
      || (day_period === 'day' && index + 1 === props.location.forecast_semi_daily.length)) {
      // The forecast day only has a "night" day_period
      // Or the forecast day only has a "day" day_period
      border = 'tw-border-b tw-border-t border-bottom-color border-top-color';
    }
    else if (day_period === 'day') {
      // The day_period is "day" and the forecast day has a "day" and a "night" day_period
      border = 'tw-border-t border-top-color';
    }
    else if (day_period === 'night') {
      // The day_period is "night" and the forecast day has a "day" and a "night" period
      border = `tw-border-t tw-border-b ${$style.nightRowTd}`;
    }

    const [
      display_at_local_label_day,
      display_at_local_label_date,
    ] = display_at_local_label.split(' ');

    const temp = day_period === 'day' ? temp_max : temp_min;

    dayPeriods.value.push({
      border,
      display_at_local_label_day,
      display_at_local_label_date,
      display_at_local_label_rowspan,
      skip_display_at_local_label,
      day_period,
      precip_accum: formatPrecip(precip_accum, units.value, true),
      precip_accum_color: getAlertColorForeground(snowLevelAlert),
      precip_snow_bar_width,
      precip_snow_range,
      precip_snow_range_color,
      slr_label,
      snowLevel: { alerts: dayPeriod.alerts, snow_level },
      conditions_icon_url,
      pop: formatPrecipChance(pop || 0),
      pop_color: getAlertColorForeground(snowLevelAlert),
      relative_humidity: formatRelativeHumidity(relative_humidity, true),
      temp_apparent_color: getAlertColorPrimary(feelsLikeAlert),
      temp_apparent: formatTemp(temp_apparent, units.value),
      temp: formatTemp(temp, units.value),
      alerts,
      wind_speed: formatWind(wind_speed, units.value, false, wind_dir_label),
      wind_gust_speed: formatWind(wind_gust_speed, units.value, false),
      wind_gust_speed_color: getAlertColorForeground(windGustAlert),
      cloud_cover: formatCloudCover(cloud_cover),
    });
  });
};

const elevationUnits = computed(() => getElevationUnits(units.value));
const firstColumnClass = 'tw-sticky tw-left-0 tw-z-10 lg:tw-static card-background-color';

const slrAttributionDetailUrl = computed(() => metaStore.getAttributionDetailUrl('cloudnine', 'slr')
  || '#');

const tableClass = [
  'tw-table-fixed tw-border-collapse',
  'tw-w-full tw-my-2.5',
  $style.table,
];

const thClass = 'tw-block tw-text-xs tw-font-bold tw-whitespace-nowrap';
const thLabelClass = `${thClass} text-light-color`;
const valueClass = 'tw-text-xs lg:tw-text-sm';
const windUnits = computed(() => getWindUnits(units.value));

const getDayPeriodClass = (dayPeriod) => [
  dayPeriod.border,
  dayPeriod.day_period === 'night' ? 'card-alternate-row-background-color' : '',
].join(' ');

const resetData = () => (dayPeriods.value = []);

const handleSelectView = async ($event, newView) => {
  const canUpdateView = updateSelectedView(newView);

  if (canUpdateView) {
    if (view.value === 'History'
      && (!props.location.history_hourly || !props.location.history_semi_daily)) {
      await nextTick();
      $emit('fetch-history-snow-detail');
    }
    else {
      resetData();
      populateData();
    }
  }
};

watch(() => props.location, () => {
  resetData();
  populateData();
  showLoading.value = false;
}, { deep: true });

populateData();
</script>

<template>
  <Card
    :has-body-padding-small="true"
  >
    <template #header>
      <div class="tw-flex tw-flex-col">
        <h2 class="section-title heading-color tw-mb-4">
          Detailed 10-Day Forecast
        </h2>
        <Pills
          :pills="views"
          :selected-pill="selectedView"
          @click="handleSelectView"
        />
      </div>
    </template>
    <template #body>
      <transition
        enter-active-class="fade-enter-transition"
        enter-from-class="fade-out"
        leave-active-class="fade-leave-transition"
        leave-to-class="fade-out"
      >
        <div
          v-if="showLoading"
          :class="loadingClassName"
        >
          <LoadingIndicator />
        </div>
      </transition>
      <div
        :class="[
          'tw-w-full',
          'tw-overflow-x-scroll lg:tw-overflow-x-hidden',
          'hide-scrollbar-on-mobile',
        ]"
      >
        <table :class="tableClass">
          <thead>
            <tr>
              <th :class="[firstColumnClass, 'tw-w-10 lg:tw-w-12']">
                &nbsp;
              </th>
              <th class="tw-w-10">
                <strong :class="thLabelClass">Temp</strong>
              </th>
              <th class="tw-w-8 lg:tw-w-10">
                &nbsp;
              </th>
              <th :class="['tw-w-12', $style.colGroupBorder]">
                <strong :class="thLabelClass">Alerts</strong>
              </th>
              <th class="tw-w-14">
                <a
                  class="hover:tw-underline link-color-brand"
                  :href="slrAttributionDetailUrl"
                  rel="noopener noreferrer"
                  target="_blank"
                >
                  <strong :class="thClass">Snow</strong>
                  <strong :class="thClass">Ratio</strong>
                </a>
              </th>
              <th
                :class="[
                  isAllAccess ? 'tw-w-24 tw-text-left' : 'tw-w-20',
                  $style.colGroupBorder,
                ]"
              >
                <strong :class="thLabelClass">12-Hr</strong>
                <strong :class="thLabelClass">Snowfall</strong>
              </th>
              <th class="tw-w-16">
                <strong :class="thLabelClass">Snow</strong>
                <strong :class="thLabelClass">Level ({{ elevationUnits }})</strong>
              </th>
              <th class="tw-w-16">
                <strong :class="thLabelClass">Precip</strong>
                <strong :class="thLabelClass">Chance</strong>
              </th>
              <th :class="['tw-w-16', $style.colGroupBorder]">
                <strong :class="thLabelClass">Precip</strong>
                <strong :class="thLabelClass">Amount</strong>
              </th>
              <th class="tw-w-14">
                <strong :class="thLabelClass">Feels</strong>
                <strong :class="thLabelClass">Like</strong>
              </th>
              <th :class="['tw-w-16', $style.colGroupBorder]">
                <strong :class="thLabelClass">Relative</strong>
                <strong :class="thLabelClass">Humidity</strong>
              </th>
              <th :class="['tw-w-28', $style.colGroupBorder]">
                <strong
                  :class="[
                    thLabelClass,
                    'tw-mx-2 tw-mb-1 tw-pb-1',
                    'tw-border-b text-light-border-color',
                  ]"
                >
                  Wind ({{ windUnits }})
                </strong>
                <div class="tw-flex tw-justify-evenly">
                  <strong :class="[thLabelClass, 'tw-w-1/2']">Speed</strong>
                  <strong :class="[thLabelClass, 'tw-w-1/2']">Gust</strong>
                </div>
              </th>
              <th class="tw-w-16">
                <strong :class="thLabelClass">Cloud</strong>
                <strong :class="thLabelClass">Cover</strong>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(dayPeriod, index) in dayPeriods"
              :key="index"
            >
              <td
                v-if="dayPeriod.skip_display_at_local_label === false"
                :class="[
                  firstColumnClass,
                  dayPeriod.border,
                  'tw-border-b border-color',
                  'tw-pl-1 tw-pr-2.5 tw-text-left',
                  dayPeriod.display_at_local_label_rowspan === 1 ? 'tw-py-0' : null,
                ]"
                :rowspan="dayPeriod.display_at_local_label_rowspan"
              >
                <strong class="tw-block tw-text-sm tw-font-normal tw-leading-4">
                  {{ dayPeriod.display_at_local_label_day }}
                </strong>
                <small class="tw-block tw-mt-0.5 tw-text-xs text-light-color tw-leading-3">
                  {{ dayPeriod.display_at_local_label_date }}
                </small>
              </td>
              <td :class="getDayPeriodClass(dayPeriod)">
                <span :class="[valueClass, 'tw-font-medium']">
                  {{ dayPeriod.temp }}
                </span>
              </td>
              <td :class="getDayPeriodClass(dayPeriod)">
                <img
                  :alt="dayPeriod.conditions_label"
                  class="tw-inline-block tw-w-7 tw-h-7 dark-invert-100%"
                  :src="dayPeriod.conditions_icon_url"
                >
              </td>
              <td
                :class="[
                  getDayPeriodClass(dayPeriod),
                  'tw-whitespace-nowrap',
                  $style.colGroupBorder,
                ]"
              >
                <span
                  v-for="alert in dayPeriod.alerts"
                  :key="alert.level.id"
                  v-tooltip="{
                    content: getAlertDescription(alert),
                    triggers: ['click', 'hover'],
                  }"
                  class="tw-inline-block tw-w-3 tw-h-3 tw-ml-px lg:tw-ml-0.5 first:tw-ml-0"
                  :style="{ backgroundColor: getAlertColorPrimary(alert) }"
                />
                <span
                  v-if="!dayPeriod.alerts.length"
                  class="tw-inline-block tw-w-3 tw-h-3 tw-border border-color"
                />
              </td>
              <td :class="getDayPeriodClass(dayPeriod)">
                <span
                  v-if="isAllAccess"
                  :class="[valueClass, 'tw-font-normal']"
                  :style="{ color: dayPeriod.precip_accum_color }"
                >
                  {{ dayPeriod.slr_label }}
                </span>
                <font-awesome-icon
                  v-else
                  class="all-access-color"
                  icon="lock"
                />
              </td>
              <td :class="[getDayPeriodClass(dayPeriod), $style.colGroupBorder]">
                <div
                  v-if="isAllAccess"
                  class="tw-flex tw-items-center tw-w-full tw-h-7"
                >
                  <span
                    class="tw-inline-block tw-rounded-r tw-h-full tw-mr-2"
                    :style="{
                      backgroundColor: dayPeriod.precip_snow_range_color,
                      width: dayPeriod.precip_snow_bar_width,
                    }"
                  />
                  <span
                    class="tw-text-sm lg:tw-text-base tw-font-medium tw-whitespace-nowrap"
                    :style="{ color: dayPeriod.precip_snow_range_color }"
                  >
                    {{ dayPeriod.precip_snow_range }}
                  </span>
                </div>
                <font-awesome-icon
                  v-else
                  class="all-access-color"
                  icon="lock"
                />
              </td>
              <td :class="getDayPeriodClass(dayPeriod)">
                <SnowLevel
                  :class="[valueClass, 'tw-font-normal']"
                  :forecast="dayPeriod.snowLevel"
                  :location="location"
                  :show-units="false"
                />
              </td>
              <td :class="getDayPeriodClass(dayPeriod)">
                <span
                  :class="[valueClass, 'tw-font-normal']"
                  :style="{ color: dayPeriod.pop_color }"
                >
                  {{ dayPeriod.pop }}
                </span>
              </td>
              <td :class="[getDayPeriodClass(dayPeriod), $style.colGroupBorder]">
                <span
                  v-if="isAllAccess"
                  :class="[valueClass, 'tw-font-normal']"
                  :style="{ color: dayPeriod.precip_accum_color }"
                >
                  {{ dayPeriod.precip_accum }}
                </span>
                <font-awesome-icon
                  v-else
                  class="all-access-color"
                  icon="lock"
                />
              </td>
              <td :class="getDayPeriodClass(dayPeriod)">
                <span
                  :class="[valueClass, 'tw-font-medium']"
                  :style="{ color: dayPeriod.temp_apparent_color }"
                >
                  {{ dayPeriod.temp_apparent }}
                </span>
              </td>
              <td :class="[getDayPeriodClass(dayPeriod), $style.colGroupBorder]">
                <span :class="[valueClass, 'tw-font-normal']">
                  {{ dayPeriod.relative_humidity }}
                </span>
              </td>
              <td :class="[getDayPeriodClass(dayPeriod), $style.colGroupBorder]">
                <div class="tw-flex tw-justify-evenly">
                  <span :class="[valueClass, 'tw-w-1/2 tw-font-normal']">
                    {{ dayPeriod.wind_speed }}
                  </span>
                  <span
                    :class="[valueClass, 'tw-w-1/2 tw-font-normal']"
                    :style="{ color: dayPeriod.wind_gust_speed_color }"
                  >
                    {{ dayPeriod.wind_gust_speed }}
                  </span>
                </div>
              </td>
              <td :class="getDayPeriodClass(dayPeriod)">
                <span :class="[valueClass, 'tw-font-normal']">
                  {{ dayPeriod.cloud_cover }}
                </span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <SnowForecastTableFooter
        field="forecast_semi_daily"
        :source-id="location.forecast_source_id"
      />
    </template>
  </Card>
</template>

<style module>
.table {
  display: table;
}

.table th {
  padding-bottom: 0.375rem;
  vertical-align: bottom;
}

.table td {
  padding-bottom: 0.375rem;
  padding-top: 0.375rem;
}

.table th,
.table td {
  text-align: center;
}

.colGroupBorder {
  border-right-color: var(--card-divider);
  border-right-width: 1px;
}

.nightRowTd {
  border-bottom-color: var(--border-color);
  border-top-color: var(--card-divider);
}
</style>
